import { createDateFormatter, useDisplayTimezone, type TimezoneMode } from "@nexus/ui";
import { memo, useMemo, useState } from "react";
import {
  defineLabels,
  resolveLabels,
  firstError,
  unwrap,
  UpdatingPill,
  Widget,
  WidgetState,
  MetricValue,
  WidgetIssueState,
} from "@nexus/widget-sdk";
import type { WidgetManifest, WidgetModule, WidgetProps } from "@nexus/widget-sdk";
import type {
  EngineHealthEntry,
  EngineHealthTenantEntry,
  EnginesHealthConfig,
  EnginesHealthData,
} from "./types";

export type {
  EngineHealthEntry,
  EngineHealthTenantEntry,
  EnginesHealthConfig,
  EnginesHealthData,
} from "./types";

export const LABELS = defineLabels([
  { key: "emptyTitle", name: "Empty state title", default: "No engines" },
  {
    key: "emptyDescription",
    name: "Empty state description",
    default: "No engine health information is available for the selected scope.",
  },
  {
    key: "unhealthyBadge",
    name: "Badge on an unhealthy engine",
    default: "Unhealthy",
  },
] as const);

export const manifest: WidgetManifest = {
  id: "engines-health",
  name: "Engines Health",
  description: "Health status for engines in the current scope.",
  version: "1.2.0",
  labels: LABELS,
  dataRequests: {
    engines: {
      source: "engines-health",
      contract: { name: "engines-health", version: 1 },
      configurable: false,
      params: { grouped: true },
    },
  },
};

export const defaultConfig: EnginesHealthConfig = {
  title: "Engines Health",
  detailHref: "/live/system-health",
};

const EMPTY_ENGINES: EngineHealthEntry[] = [];

// Kept compact on one page for the common 8–10 case; beyond that a minimal
// pager kicks in. Unhealthy engines are always sorted onto the first page.
const PAGE_SIZE = 10;

function formatLatestData(value: string, timezone: TimezoneMode): string {
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return value;
  return createDateFormatter(timezone, {
    month: "short",
    day: "numeric",
    hour: "2-digit",
    minute: "2-digit",
  }).format(date);
}

function tenantRef(tenant: EngineHealthTenantEntry): string {
  return `${tenant.tenant_label} (#${tenant.tenant_id})`;
}

// A single multi-line string surfaced through the native tooltip: it never gets
// clipped by the widget's overflow, yet still carries the full drill-down detail
// (status, tenant coverage, offending tenant ids, freshness, and the affordance).
function buildEngineTooltip(entry: EngineHealthEntry, timezone: TimezoneMode): string {
  const healthy = entry.healthcheck_status;
  const tenants = entry.tenants ?? [];
  const problems = entry.problem_tenants ?? [];
  const healthyCount = tenants.filter((tenant) => tenant.healthcheck_status).length;

  const lines = [
    `${entry.engine_label} — ${healthy ? "HEALTHY" : "UNHEALTHY"}`,
    `Tenants: ${healthyCount}/${tenants.length || 1} healthy`,
  ];

  if (!healthy && problems.length > 0) {
    lines.push(`Problem tenants: ${problems.map(tenantRef).join(", ")}`);
  }

  lines.push(`Latest data: ${formatLatestData(entry.latest_data, timezone)}`);
  lines.push("Click to open health details →");

  return lines.join("\n");
}

/**
 * StatusDot — Phase 5 fix.
 * animate-ping motion-reduce:animate-none runs ONLY on problem states (unhealthy/error).
 * Healthy engines show a calm static dot — pulsing green 7× signals nothing wrong
 * and wastes the escalation budget.
 */
function StatusDot({ healthy }: { healthy: boolean }) {
  if (healthy) {
    // Static dot: no animation — calm at rest.
    return (
      <span className="relative flex h-2 w-2 shrink-0" aria-hidden="true">
        <span className="relative inline-flex h-2 w-2 rounded-full bg-status-ok" />
      </span>
    );
  }
  return (
    <span className="relative flex h-2 w-2 shrink-0" aria-hidden="true">
      <span className="absolute inline-flex h-full w-full animate-ping motion-reduce:animate-none rounded-full bg-status-error opacity-60" />
      <span className="relative inline-flex h-2 w-2 rounded-full bg-status-error" />
    </span>
  );
}

function Chevron() {
  return (
    <svg
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth="2.5"
      strokeLinecap="round"
      strokeLinejoin="round"
      aria-hidden="true"
      className="h-3 w-3 shrink-0 -translate-x-1 opacity-0 transition-all duration-150 group-hover:translate-x-0 group-hover:opacity-100 group-focus-visible:translate-x-0 group-focus-visible:opacity-100"
    >
      <path d="m9 18 6-6-6-6" />
    </svg>
  );
}

const EngineBlock = memo(function EngineBlock({
  entry,
  href,
  unhealthyLabel,
}: {
  entry: EngineHealthEntry;
  href: string;
  unhealthyLabel: string;
}) {
  const healthy = entry.healthcheck_status;
  const problemCount = (entry.problem_tenants ?? []).length;
  const timezone = useDisplayTimezone();

  return (
    <li>
      <a
        href={href}
        title={buildEngineTooltip(entry, timezone)}
        aria-label={`${entry.engine_label} — ${healthy ? "healthy" : "unhealthy"}. Open health details.`}
        className={[
          "group relative flex items-center gap-2 rounded-lg border px-3 py-3 transition-colors duration-150",
          "focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-inset focus-visible:ring-focus",
          healthy
            ? "border-border bg-surface hover:border-brand-cyan/40 hover:bg-surface-elevated"
            : "border-status-error/30 bg-status-error/[0.06] hover:border-status-error/60 hover:bg-status-error/[0.10]",
        ].join(" ")}
      >
        <StatusDot healthy={healthy} />

        <span
          className={[
            "min-w-0 truncate text-sm leading-tight tracking-tight",
            healthy
              ? "font-medium text-secondary group-hover:text-primary"
              : "font-semibold text-primary",
          ].join(" ")}
        >
          {entry.engine_label}
        </span>

        {!healthy && (
          <span className="shrink-0 rounded bg-status-error/10 px-1.5 py-[1px] text-xs font-semibold leading-none tracking-wide text-status-error-text ring-1 ring-status-error/20">
            {problemCount > 0
              ? `${problemCount} tenant${problemCount === 1 ? "" : "s"}`
              : unhealthyLabel}
          </span>
        )}

        {/* The status stays beside the name, however wide the tile. */}
        <span className="ml-auto" aria-hidden />
        <Chevron />
      </a>
    </li>
  );
});

// auto-fit: a few engines share the full width, many wrap into even columns.
const ENGINE_GRID =
  "grid grid-cols-1 gap-2 @lg:grid-cols-[repeat(auto-fit,minmax(16rem,1fr))] @[96rem]:grid-cols-[repeat(auto-fit,minmax(20rem,1fr))]";

function EnginesHealthComponent({ data, config }: WidgetProps<EnginesHealthConfig>) {
  const labels = resolveLabels(LABELS, config);
  const error = firstError(data, ["engines"]);
  const resolved = unwrap<EnginesHealthData>(data.engines);
  const isLoading = data.engines?.isLoading ?? false;

  const engines = resolved?.engines ?? EMPTY_ENGINES;
  const title = config.title ?? defaultConfig.title ?? "Engines Health";
  const detailHref = config.detailHref ?? defaultConfig.detailHref ?? "/live/system-health";

  const { sorted, downCount } = useMemo(() => {
    const next = [...engines].sort((a, b) => {
      // Unhealthy first, then alphabetical — problems cluster at the top-left.
      if (a.healthcheck_status !== b.healthcheck_status) return a.healthcheck_status ? 1 : -1;
      return a.engine_label.localeCompare(b.engine_label);
    });
    const down = engines.reduce((count, engine) => count + (engine.healthcheck_status ? 0 : 1), 0);
    return { sorted: next, downCount: down };
  }, [engines]);

  const [page, setPage] = useState(0);
  const pageCount = Math.max(1, Math.ceil(sorted.length / PAGE_SIZE));
  const safePage = Math.min(page, pageCount - 1);
  const pageItems =
    pageCount > 1 ? sorted.slice(safePage * PAGE_SIZE, safePage * PAGE_SIZE + PAGE_SIZE) : sorted;

  if (error) {
    console.error("[EnginesHealthWidget] Failed to load data:", error);

    return (
      <Widget title={title}>
        <WidgetIssueState error={error} />
      </Widget>
    );
  }

  if (engines.length === 0 && isLoading) {
    return (
      <Widget title={title}>
        <div className="px-3 py-2.5">
          <ul className={ENGINE_GRID}>
            {Array.from({ length: 6 }, (_, index) => (
              <li
                key={index}
                className="flex items-center gap-2 rounded-lg border border-border bg-surface px-2.5 py-2"
              >
                <span className="h-2 w-2 shrink-0 rounded-full skeleton" />
                <span className="h-3 flex-1 rounded skeleton" />
              </li>
            ))}
          </ul>
        </div>
      </Widget>
    );
  }

  if (engines.length === 0) {
    return (
      <Widget title={title}>
        <WidgetState title={labels.emptyTitle} description={labels.emptyDescription} />
      </Widget>
    );
  }

  return (
    <Widget
      title={title}
      actions={<div className="flex items-center gap-2">{isLoading && <UpdatingPill />}</div>}
    >
      <div className="@container flex h-full flex-col p-4">
        <div className="mb-4 flex flex-wrap items-end justify-between gap-4 hidden">
          <div>
            <p className="mb-1 text-metric-label text-secondary">Healthy engines</p>
            <div className="flex items-baseline gap-2">
              <MetricValue>{engines.length - downCount}</MetricValue>
              <span className="text-metric-caption tabular-nums text-muted">
                / {engines.length}
              </span>
            </div>
          </div>
          <p
            className={`text-metric-caption ${downCount ? "text-status-error-text" : "text-muted"}`}
          >
            {downCount ? `${downCount} require investigation` : "No engine health issues reported"}
          </p>
        </div>
        <div className="mb-4 flex h-1.5 gap-1 hidden" aria-hidden="true">
          {sorted.map((engine) => (
            <span
              key={engine.engine_id}
              className={`min-w-0 flex-1 rounded-full ${engine.healthcheck_status ? "bg-status-ok/60" : "bg-status-error"}`}
            />
          ))}
        </div>
        <ul className={ENGINE_GRID}>
          {pageItems.map((entry) => (
            <EngineBlock
              key={entry.engine_id}
              entry={entry}
              href={detailHref}
              unhealthyLabel={labels.unhealthyBadge}
            />
          ))}
        </ul>

        {pageCount > 1 && (
          <div className="mt-2 flex items-center justify-end gap-1.5 text-xs text-muted">
            <button
              type="button"
              onClick={() => setPage(Math.max(0, safePage - 1))}
              disabled={safePage === 0}
              aria-label="Previous page"
              className="inline-flex h-6 w-6 items-center justify-center rounded-md border border-border leading-none transition-colors hover:bg-surface-elevated disabled:opacity-40 disabled:hover:bg-transparent"
            >
              ‹
            </button>
            <span className="tabular-nums">
              {safePage + 1}/{pageCount}
            </span>
            <button
              type="button"
              onClick={() => setPage(Math.min(pageCount - 1, safePage + 1))}
              disabled={safePage === pageCount - 1}
              aria-label="Next page"
              className="inline-flex h-6 w-6 items-center justify-center rounded-md border border-border leading-none transition-colors hover:bg-surface-elevated disabled:opacity-40 disabled:hover:bg-transparent"
            >
              ›
            </button>
          </div>
        )}
      </div>
    </Widget>
  );
}

export const Component = EnginesHealthComponent;

const widgetModule: WidgetModule<EnginesHealthConfig> = {
  manifest,
  Component,
  defaultConfig,
};

export default widgetModule;
